import { UseMutateFunction } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { JSX, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "@/components/buttons/Button.tsx";
import GroupTransition from "@/components/helpers/GroupTransition/GroupTransition.tsx";

import { cn } from "@/utils/cn.ts";

const timer = 60;

interface ResendOtpButtonProps {
  mutate: UseMutateFunction<any, any, string, unknown>;
  isPending: boolean;
  username: string;
  className?: string;
}

const ResendOtpButton = ({ mutate, isPending, username, className }: ResendOtpButtonProps): JSX.Element => {
  const { t } = useTranslation();
  const [secondsLeft, setSecondsLeft] = useState<number>(timer);
  const [error, setError] = useState<string | null>(null);

  const handleClick = (): void => {
    mutate(username, {
      onSuccess: () => {
        setError(null);
        setSecondsLeft(timer);
      },
      onError: (err: AxiosError) => {
        if (err?.response?.status === 429) {
          setError(t("Auth.too-many-attempts"));
        } else {
          setError(t("error.something-wrong"));
        }
      },
    });
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const isDisabled = secondsLeft > 0;

  return (
    <GroupTransition<HTMLDivElement> transitionKey={isDisabled.toString()}>
      {({ ref }) => (
        <div ref={ref} className={cn("w-full space-y-4 text-center", className)}>
          {!isDisabled ? (
            <Button
              size="medium"
              type={"button"}
              color={"black"}
              disabled={isPending}
              aria-label={t("auth.resend-otp")}
              onClick={handleClick}
              className={cn("uppercase", secondsLeft > 0 && "text-on-surface")}
            >
              {t("auth.resend-otp")}
            </Button>
          ) : (
            <p className={"text-on-surface flex justify-center leading-[1.313rem]"}>
              {t("auth.resend-otp-second", {
                value: secondsLeft,
              })}
            </p>
          )}
          {error && <p className={"text-error-1"}>{error}</p>}
        </div>
      )}
    </GroupTransition>
  );
};

export default ResendOtpButton;
